import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import useToast from '../../../../../state/hooks/useToast';
import useAlert from '../../../../../state/hooks/useAlert';
import { changePageInfo } from '../../../../../state/store/reducers/ui/headerSlice';
import { Button, Pagination } from '../../../../../components/common';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import appCategoryService from '../../../../../services/api/apps/appCategoryService';
import { ListState } from '../../../../../components/blocks';
import { paginationInfo } from '../../../../../utils/pagination';
import { Menu } from '@headlessui/react';
import PopoverWithFloat from '../../../../../components/hoc/PopoverWithFloat';
import { format } from 'date-fns';
import clsx from 'clsx';
import ModalAppCategoryForm from '../modals/ModalAppCategoryForm';


function AppCategoryListPage() {
    const dispatch = useDispatch();
    const [searchParams, setSearchParams] = useSearchParams();

    const { addToast } = useToast();
    const { openAlertConfirm } = useAlert();

    const [categoryForm, setCategoryForm] = useState({ open: false, category: null });



    // get query params
    const query: any = {
        pager: {
            page: Number(searchParams.get('page')) || 1,
        },
        filters: {},
    };
    //if (searchParams.get('name')) { query.filters['name'] = searchParams.get('name') || '' }
    //if (searchParams.get('status')) { query.filters['status'] = searchParams.get('status') || '' }


    useEffect(() => {
        dispatch(changePageInfo({ title: "App Categories" }));
    }, []);


    const queryKey = ['payments', 'apps', 'categories', query];
    const queryClient = useQueryClient();
    const dataQuery = useQuery({
        queryKey: queryKey,
        queryFn: () => appCategoryService.getCategories({ ...query.pager, ...query.filters }),
        staleTime: Infinity,
        retryDelay: Infinity,
        refetchOnWindowFocus: false, // Disable refetching on window focus
        refetchOnMount: false, // Disable refetching on component mount
        refetchInterval: false, // Disable interval-based refetching
    });


    return (
        <div>
            <div className='flex flex-row-reverse flex-wrap justify-between items-start mb-1'>
                <Button type='button' onClick={() => setCategoryForm({ open: true, category: null })} className='mb-4'>
                    Create Category
                </Button>
            </div>


            {
                !dataQuery.isError && dataQuery.isFetched && dataQuery.isFetched && dataQuery.data?.data && dataQuery.data?.data.length > 0 &&
                <React.Fragment>
                    <div className='max-w-full overflow-x-auto mb-6'>
                        <table className="border-collapse table-auto w-full">
                            <thead>
                                <tr className='text-left border-b-2 dark:border-gray-700'>
                                    <th className='py-6 px-6'>Name</th>
                                    <th className='py-6 px-6'>Description</th>
                                    <th className='py-6 px-6'>Code</th>
                                    <th className='py-6 px-6'>Status</th>
                                    <th className='py-6 px-6'>Date Created</th>
                                    {/*  <th className='py-6 px-6 text-right'>Action</th> */}
                                </tr>
                            </thead>
                            <tbody className="divide-y dark:divide-gray-700/60">
                                {
                                    dataQuery.data?.data.map((dataItem: any) =>
                                        <tr key={dataItem.id}>
                                            <td className='py-4 px-6'>{dataItem.name}</td>
                                            <td className='py-4 px-6' title={dataItem.description}>
                                                <p className='line-clamp-1'>{dataItem.description}</p>
                                            </td>
                                            <td className='py-4 px-6'>{dataItem.code}</td>
                                            <td className='py-4 px-6'>
                                                <span className={clsx('text-sm font-medium rounded-lg py-1.5 px-2.5', {
                                                    'bg-green-600 text-white': dataItem.status === 'ACTIVE',
                                                    'bg-yellow-600 text-white': dataItem.status === 'INACTIVE',
                                                })}>
                                                    {dataItem.status}
                                                </span>
                                            </td>
                                            <td className='py-4 px-6 whitespace-nowrap'>{format(new Date(dataItem.createdDate), 'd LLL yyyy h:mm a')}</td>
                                        </tr>
                                    )
                                }
                            </tbody>
                        </table>
                    </div>

                    <div className='flex justify-between flex-wrap'>
                        <p className='mb-3 mr-8 text-gray-500 dark:text-gray-400'>{paginationInfo(dataQuery.data.pager.pageSize, dataQuery.data.pager.totalCount, dataQuery.data?.pager.page)}</p>

                        <Pagination
                            currentPage={dataQuery.data?.pager.page}
                            totalCount={dataQuery.data?.pager.totalCount}
                            pageSize={dataQuery.data.pager.pageSize}
                            onPageChange={(page) => {
                                setSearchParams(current => ({
                                    ...Object.fromEntries(current.entries()),
                                    page: page.toString(),
                                }));
                            }}
                        />
                    </div>
                </React.Fragment>
            }


            <ListState
                hasSearch={Object.keys(query.filters).length > 0}
                listCount={dataQuery.data?.data.length || 0}
                totalDataCount={dataQuery.data?.pager.total || 0}
                isLoading={dataQuery.isLoading}
                isError={dataQuery.isError}
                onTryAgain={() => dataQuery.refetch()}
            />


            <ModalAppCategoryForm
                open={categoryForm.open}
                category={categoryForm.category}
                onAdded={(data) => {
                    queryClient.invalidateQueries({ queryKey });
                    addToast({
                        text: `Category created successfully`,
                        type: "success"
                    });
                }}
                onUpdated={(data) => {
                    addToast({
                        text: `Category updated successfully`,
                        type: "success"
                    });
                }}
                onClose={() => setCategoryForm((prevState) => ({ ...prevState, open: false }))}
            />
        </div>
    );
}

export default AppCategoryListPage;